/**
 * Dashboard HR (spec 01 §1) — hero, KPI i lista „Co wymaga uwagi".
 *
 * Każda liczba pochodzi z agregatu po danych tenanta. Pusta firma nie dostaje
 * czterech pozycji z makiety, tylko hero zachęcające do pierwszego zaproszenia
 * i pustą listę [D19]. Kafel listy płac pokazuje koszt z chwili naliczenia —
 * dopóki nikt nie naliczył okresu, nie ma tam żadnej kwoty.
 *
 * „Następne kroki" (spec 01 §1 pkt 4, fala 8, grupa 118) to kolejka działań
 * z faktów: weryfikacja listy płac, deklaracje za zamknięty miesiąc, wnioski
 * urlopowe i zatrudnienia w toku. Bez faktów sekcji nie ma [D19].
 */
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { Button, Card, EmptyState, Check, ChevronRight, Spinner, StatCard } from '@k1p4/ui'
import { formatDate, formatPLN } from '@k1p4/i18n'
import { api } from '../../api/client'
import { useAuth, useHasRole } from '../../auth/AuthProvider'
import { canOpenAppPath } from '../../nav'
import { useFormat } from '../../format'
import styles from './Dashboard.module.css'

/** Dokąd prowadzi pozycja „wymaga uwagi" — jedna mapa zamiast if-ów w JSX. */
const ATTENTION_TARGET: Record<string, string> = {
  missing_documents: '/app/employees',
  open_period: '/app/time',
  ending_contract: '/app/employees',
  pending_leaves: '/app/leaves',
  pending_requests: '/app/requests',
  unapproved_time: '/app/time',
  expiring_health: '/app/health',
  payroll_review: '/app/payroll',
}

/** Dokąd prowadzi „następny krok". */
const NEXT_STEP_TARGET: Record<string, string> = {
  verify_payroll: '/app/payroll',
  prepare_declarations: '/app/declarations',
  send_declarations: '/app/declarations',
  decide_leaves: '/app/leaves',
  finish_onboarding: '/app/employees',
}

export function Dashboard() {
  const { t } = useTranslation()
  const fmt = useFormat()
  const navigate = useNavigate()
  const canInvite = useHasRole('hr_admin')
  const roles = useAuth().session?.user.roles ?? []

  const dashboard = useQuery({ queryKey: ['dashboard', 'hr'], queryFn: () => api.hrDashboard() })

  if (dashboard.isPending) return <Spinner label={t('common.loading')} />
  if (dashboard.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const { kpi, attention, payroll } = dashboard.data
  const nextSteps = dashboard.data.nextSteps ?? []
  const empty = kpi.employees === 0

  return (
    <>
      <section className={styles.hero}>
        <span className={styles.heroBadge}>
          <span className={styles.heroDot} aria-hidden="true" />
          {t('hrDashboard.heroBadge')}
        </span>
        <h2 className={styles.heroTitle}>
          {t(empty ? 'hrDashboard.heroTitleEmpty' : 'hrDashboard.heroTitle')}
        </h2>
        <p className={styles.heroBody}>
          {t(empty ? 'hrDashboard.heroBodyEmpty' : 'hrDashboard.heroBody')}
        </p>
        {empty && canInvite ? (
          <Button onClick={() => navigate('/app/employees')}>{t('hrDashboard.heroCta')}</Button>
        ) : null}
      </section>

      <div className={styles.kpiGrid}>
        <StatCard
          label={t('hrDashboard.kpi.employees')}
          value={kpi.activeEmployees}
          note={t('hrDashboard.kpi.employeesNote')}
        />
        <StatCard
          label={t('hrDashboard.kpi.requests')}
          value={kpi.openRequests}
          note={t('hrDashboard.kpi.requestsNote')}
          highlighted={kpi.openRequests > 0}
          {...(canOpenAppPath(roles, '/app/requests')
            ? { onClick: () => navigate('/app/requests') }
            : {})}
        />
        <StatCard
          label={t('hrDashboard.kpi.ending')}
          value={kpi.endingContracts}
          note={t('hrDashboard.kpi.endingNote')}
        />
        <StatCard
          label={t('hrDashboard.kpi.payroll')}
          value={
            payroll.available && payroll.employerCostGr !== null
              ? formatPLN(payroll.employerCostGr)
              : t('hrDashboard.kpi.payrollNone')
          }
          note={
            payroll.available
              ? t('hrDashboard.kpi.payrollPeriod', {
                  period: payroll.month.split('-').toReversed().join('/'),
                  count: payroll.peopleCount,
                })
              : t('hrDashboard.kpi.payrollNote')
          }
          highlighted={payroll.available && payroll.status === 'weryfikacja'}
          {...(payroll.available ? { onClick: () => navigate(`/app/payroll/${payroll.id}`) } : {})}
        />
        <StatCard
          label={t('hrDashboard.kpi.documents')}
          value={kpi.missingDocuments}
          note={t('hrDashboard.kpi.documentsNote')}
        />
      </div>

      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>{t('hrDashboard.attentionTitle')}</h3>
          <span className={styles.sectionMeta}>{t('hrDashboard.attentionSubtitle')}</span>
        </div>

        {attention.length === 0 ? (
          <EmptyState
            icon={<Check size={28} />}
            title={t('hrDashboard.attentionEmpty')}
            description={t('hrDashboard.attentionEmptyBody')}
          />
        ) : (
          <Card pad="flush" className={styles.list}>
            {attention.map((item, index) => {
              /* Cel poza zasięgiem roli: wiersz informuje, ale nie prowadzi na ekran odmowy (grupa 160). */
              const target = ATTENTION_TARGET[item.key]
              /* Fakty z API formatujemy tutaj, w języku użytkownika (fala 9, grupa 151). */
              const meta = item.month
                ? fmt.month(item.month, 'period')
                : item.person
                  ? item.date
                    ? `${item.person} · ${fmt.date(item.date, 'dot')}`
                    : item.person
                  : item.meta
              const Row = target !== undefined && canOpenAppPath(roles, target) ? 'button' : 'div'
              return (
              <Row
                key={`${item.key}-${meta ?? index}`}
                {...(Row === 'button'
                  ? { type: 'button' as const, onClick: () => navigate(target!) }
                  : {})}
                className={Row === 'button' ? styles.row : `${styles.row} ${styles.rowStatic}`}
              >
                <span className={styles.rowIndex}>{index + 1}</span>
                <span className={styles.rowBody}>
                  <span className={styles.rowTitle}>
                    {t(`hrDashboard.attention.${item.key}`, { count: item.count })}
                  </span>
                  {meta ? (
                    <span className={styles.rowMeta}>
                      {t(`hrDashboard.attention.${item.key}Meta`, {
                        meta,
                        defaultValue: meta,
                      })}
                    </span>
                  ) : null}
                </span>
                {Row === 'button' ? (
                  <span className={styles.rowCta}>
                    {t('hrDashboard.attention.cta')}
                    <ChevronRight size={16} />
                  </span>
                ) : null}
              </Row>
              )
            })}
          </Card>
        )}
      </section>

      {nextSteps.length > 0 ? (
        <section className={styles.section}>
          <div className={styles.sectionHead}>
            <h3 className={styles.sectionTitle}>{t('hrDashboard.nextSteps.title')}</h3>
            <span className={styles.sectionMeta}>{t('hrDashboard.nextSteps.subtitle')}</span>
          </div>
          <Card pad="flush" className={styles.list}>
            {nextSteps.map((step, index) => {
              const target = NEXT_STEP_TARGET[step.key]
              const Row = target !== undefined && canOpenAppPath(roles, target) ? 'button' : 'div'
              const values = {
                count: step.count,
                /* Kroki płacowe niosą miesiąc „rrrr-mm" — słownie w zdaniu (grupa 151). */
                meta:
                  step.meta && /^\d{4}-\d{2}$/.test(step.meta) ? fmt.month(step.meta, 'period') : (step.meta ?? ''),
                deadline: step.deadline ? formatDate(step.deadline, 'dot') : '',
              }
              return (
                <Row
                  key={step.key}
                  {...(Row === 'button'
                    ? { type: 'button' as const, onClick: () => navigate(target!) }
                    : {})}
                  className={Row === 'button' ? styles.row : `${styles.row} ${styles.rowStatic}`}
                >
                  <span className={styles.rowIndex}>{index + 1}</span>
                  <span className={styles.rowBody}>
                    <span className={styles.rowTitle}>{t(`hrDashboard.nextSteps.${step.key}`, values)}</span>
                    <span className={styles.rowMeta}>
                      {t(
                        step.key === 'send_declarations' && !step.deadline
                          ? 'hrDashboard.nextSteps.send_declarationsMetaNoDeadline'
                          : `hrDashboard.nextSteps.${step.key}Meta`,
                        values,
                      )}
                    </span>
                  </span>
                  {Row === 'button' ? (
                    <span className={styles.rowCta}>
                      {t('hrDashboard.attention.cta')}
                      <ChevronRight size={16} />
                    </span>
                  ) : null}
                </Row>
              )
            })}
          </Card>
        </section>
      ) : null}
    </>
  )
}
